import React from 'react';
import { useTranslation } from 'react-i18next';
import SystemUserFormModal from './system-user-form-modal.jsx';
import SupportAccessGrantModal from './support-access-grant-modal.jsx';
import SupportAccessLinkModal from './support-access-link-modal.jsx';
import FloatingAlerts from '../../../shared/ui/floating-alerts/floating-alerts.jsx';
import LoadingContainer from '../../../shared/ui/loading-container/loading-container.jsx';

function formatDateTime(value) {
  const raw = String(value || '').trim();
  if (!raw) {
    return '-';
  }

  const date = new Date(raw);
  if (Number.isNaN(date.getTime())) {
    return raw;
  }

  return date.toLocaleString();
}

function getStatusBadgeClass(status) {
  const normalized = String(status || '').trim().toLowerCase();
  if (normalized === 'active') {
    return 'bg-success-subtle text-success';
  }
  if (normalized === 'revoked') {
    return 'bg-danger-subtle text-danger';
  }

  return 'app-badge-disabled';
}

function SystemUsersPageView({
  activeTab,
  rows,
  supportRows,
  isLoading,
  isSupportLoading,
  isSaving,
  error,
  success,
  canCreate,
  canUpdate,
  canViewSupportAccess,
  canCreateSupportAccess,
  canUpdateSupportAccess,
  isCreateOpen,
  isSupportGrantOpen,
  editingTargetId,
  form,
  formErrors,
  supportGrantForm,
  supportGrantErrors,
  oneTimeInviteUrl,
  setActiveTab,
  openDetails,
  openCreate,
  openSupportGrant,
  openEdit,
  closeSupportGrant,
  closeModals,
  onFormChange,
  onSupportGrantFormChange,
  saveEdit,
  saveCreate,
  saveSupportGrant,
  revokeSupportGrant,
  regenerateSupportInvite,
  closeSupportInvite,
  toggleUserDisabled,
  isCurrentUserRow,
}) {
  const { t } = useTranslation();
  const showDisabledInEdit = !(editingTargetId && isCurrentUserRow({ id: editingTargetId, email: form.email }));
  const isTenantUsersTab = activeTab === 'tenant-users';
  const isSupportAccessTab = activeTab === 'support-access';

  return (
    <div>
      <FloatingAlerts
        info={isSaving ? t('systemUsers.savingInfo') : ''}
        error={error}
        success={success}
      />
      <div className="app-page-header d-flex align-items-start justify-content-between gap-3">
        <div>
          <h2>{t('systemUsers.title')}</h2>
          <p>{t('systemUsers.subtitle')}</p>
        </div>
        {isTenantUsersTab && canCreate ? (
          <button
            type="button"
            className="btn btn-primary"
            onClick={openCreate}
            disabled={isLoading || isSaving}
          >
            {t('systemUsers.addUser')}
          </button>
        ) : null}
        {isSupportAccessTab && canCreateSupportAccess ? (
          <button
            type="button"
            className="btn btn-primary"
            onClick={openSupportGrant}
            disabled={isSupportLoading || isSaving}
          >
            {t('systemUsers.grantSupportAccess')}
          </button>
        ) : null}
      </div>

      <ul className="nav nav-tabs mb-3">
        <li className="nav-item">
          <button
            type="button"
            className={`nav-link ${isTenantUsersTab ? 'active' : ''}`}
            onClick={() => setActiveTab('tenant-users')}
          >
            {t('systemUsers.tenantUsersTab')}
          </button>
        </li>
        {canViewSupportAccess ? (
          <li className="nav-item">
            <button
              type="button"
              className={`nav-link ${isSupportAccessTab ? 'active' : ''}`}
              onClick={() => setActiveTab('support-access')}
            >
              {t('systemUsers.supportAccessTab')}
            </button>
          </li>
        ) : null}
      </ul>

      <div className="widget-content searchable-container list">
        <div className="card card-body">
          {isTenantUsersTab ? (
            <LoadingContainer isLoading={isLoading} hasData={rows.length > 0} initialText={t('common.loading')} className="table-responsive">
              <table className="table search-table align-middle text-nowrap mb-0">
                <thead className="header-item">
                  <tr>
                    <th>{t('systemUsers.colName')}</th>
                    <th>{t('systemUsers.colUsername')}</th>
                    <th style={{ width: '1%', whiteSpace: 'nowrap' }} className="text-center">{t('systemUsers.colDisabled')}</th>
                    <th style={{ width: '1%', whiteSpace: 'nowrap' }} className="text-end">{t('systemUsers.colActions')}</th>
                  </tr>
                </thead>
                <tbody>
                  {rows.length === 0 ? (
                    <tr>
                      <td colSpan="4" className="text-center text-muted">{t('systemUsers.noUsersFound')}</td>
                    </tr>
                  ) : null}

                  {rows.map((row) => (
                    <tr key={row.id} className="app-clickable-row" onClick={() => openDetails(row.id)}>
                      <td>{row.fullName}</td>
                      <td>{row.email || '-'}</td>
                      <td className="text-center">
                        {!canUpdate ? (
                          <span className="text-muted">-</span>
                        ) : isCurrentUserRow(row) ? (
                          <span className="text-muted">-</span>
                        ) : (
                          <div className="form-check form-switch d-inline-flex m-0">
                            <input
                              type="checkbox"
                              className="form-check-input"
                              checked={Boolean(row.disabled)}
                              onChange={(event) => {
                                event.stopPropagation();
                                toggleUserDisabled(row, event.target.checked);
                              }}
                              onClick={(event) => event.stopPropagation()}
                              disabled={isSaving}
                              title="Disabled"
                            />
                          </div>
                        )}
                      </td>
                      <td className="text-nowrap text-end">
                        {canUpdate ? (
                          <div className="d-flex align-items-center justify-content-end gap-2">
                            <button
                              type="button"
                              className="btn btn-sm btn-primary"
                              title="Edit"
                              onClick={(event) => {
                                event.stopPropagation();
                                openEdit(row);
                              }}
                              disabled={isSaving}
                            >
                              <i className="fa fa-pen"></i>
                            </button>
                          </div>
                        ) : null}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </LoadingContainer>
          ) : null}

          {isSupportAccessTab ? (
            <LoadingContainer isLoading={isSupportLoading} hasData={supportRows.length > 0} initialText={t('common.loading')} className="table-responsive">
              <table className="table search-table align-middle text-nowrap mb-0">
                <thead className="header-item">
                  <tr>
                    <th style={{ minWidth: '180px' }}>{t('systemUsers.supportColLabel')}</th>
                    <th style={{ minWidth: '130px' }}>{t('systemUsers.supportColAccessLevel')}</th>
                    <th style={{ minWidth: '110px' }}>{t('systemUsers.supportColStatus')}</th>
                    <th style={{ minWidth: '160px' }}>{t('systemUsers.supportColExpires')}</th>
                    <th style={{ minWidth: '110px' }}>{t('systemUsers.supportColInvite')}</th>
                    <th style={{ minWidth: '160px' }}>{t('systemUsers.supportColLastLogin')}</th>
                    <th style={{ minWidth: '150px' }}>{t('systemUsers.supportColCreatedBy')}</th>
                    <th style={{ width: '1%', whiteSpace: 'nowrap' }} className="text-end">{t('systemUsers.colActions')}</th>
                  </tr>
                </thead>
                <tbody>
                  {supportRows.length === 0 ? (
                    <tr>
                      <td colSpan="8" className="text-center text-muted">{t('systemUsers.noSupportAccessFound')}</td>
                    </tr>
                  ) : null}

                  {supportRows.map((row) => (
                    <tr key={row.key || row.id}>
                      <td>{row.label || '-'}</td>
                      <td>{row.accessLevelLabel || row.accessLevel || '-'}</td>
                      <td>
                        <span className={`badge ${getStatusBadgeClass(row.status)}`}>
                          {row.statusLabel || row.status || '-'}
                        </span>
                      </td>
                      <td>{formatDateTime(row.expiresAt)}</td>
                      <td>{row.inviteStatusLabel || '-'}</td>
                      <td>{formatDateTime(row.lastLoginAt)}</td>
                      <td>{row.createdBy || '-'}</td>
                      <td className="text-nowrap text-end">
                        {canUpdateSupportAccess ? (
                          <div className="d-flex align-items-center justify-content-end gap-2">
                            <button
                              type="button"
                              className="btn btn-sm btn-outline-secondary"
                              onClick={() => regenerateSupportInvite(row)}
                              disabled={!row.canRegenerateInvite || isSaving || isSupportLoading}
                            >
                              {t('systemUsers.regenerateInvite')}
                            </button>
                            <button
                              type="button"
                              className="btn btn-sm btn-outline-danger"
                              onClick={() => revokeSupportGrant(row)}
                              disabled={!row.canRevoke || isSaving || isSupportLoading}
                            >
                              {t('systemUsers.revokeSupportAccess')}
                            </button>
                          </div>
                        ) : null}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </LoadingContainer>
          ) : null}
        </div>
      </div>

      {editingTargetId ? (
        <SystemUserFormModal
          title={t('systemUsers.editUser')}
          isSaving={isSaving}
          form={form}
          formErrors={formErrors}
          showDisabled={showDisabledInEdit}
          onFormChange={onFormChange}
          onSubmit={saveEdit}
          onClose={closeModals}
          submitLabel={t('systemUsers.submitSave')}
          includeConfirmPassword={false}
        />
      ) : null}

      {isCreateOpen ? (
        <SystemUserFormModal
          title={t('systemUsers.addUser')}
          isSaving={isSaving}
          form={form}
          formErrors={formErrors}
          showDisabled={false}
          onFormChange={onFormChange}
          onSubmit={saveCreate}
          onClose={closeModals}
          submitLabel={t('systemUsers.submitAdd')}
          includeConfirmPassword
        />
      ) : null}

      {isSupportGrantOpen ? (
        <SupportAccessGrantModal
          isSaving={isSaving}
          form={supportGrantForm}
          formErrors={supportGrantErrors}
          onFormChange={onSupportGrantFormChange}
          onSubmit={saveSupportGrant}
          onClose={closeSupportGrant}
        />
      ) : null}

      {oneTimeInviteUrl ? (
        <SupportAccessLinkModal
          inviteUrl={oneTimeInviteUrl}
          onClose={closeSupportInvite}
        />
      ) : null}
    </div>
  );
}

export default SystemUsersPageView;
